/**
 * Utilitaires pour la gestion des permissions avancées
 * Fournit des helpers pour travailler avec le nouveau système de permissions
 */

import { ACTIONS } from '../config/advancedRolesConfig';

// Mapping des labels et descriptions pour les actions
const ACTION_METADATA = {
  read: { label: 'Lecture', description: 'Visualiser et consulter les données', level: 1 },
  write: { label: 'Écriture', description: 'Modifier les données existantes', level: 2 },
  create: { label: 'Création', description: 'Créer de nouveaux éléments', level: 3 },
  delete: { label: 'Suppression', description: 'Supprimer des éléments', level: 4 },
  manage: { label: 'Gestion', description: 'Contrôle complet (inclut toutes les autres)', level: 5 }
};

// Dépendances entre les actions
const ACTION_DEPENDENCIES = {
  write: ['read'],
  create: ['read'],
  delete: ['read'],
  manage: ['read', 'write', 'create', 'delete']
};

/**
 * Obtient les informations détaillées d'une action
 * @param {string} actionId - ID de l'action (read, write, create, delete, manage)
 * @returns {Object} Informations détaillées de l'action
 */
export const getActionInfo = (actionId) => {
  const metadata = ACTION_METADATA[actionId] || {};
  return {
    id: actionId,
    label: metadata.label || actionId,
    description: metadata.description || '',
    level: metadata.level || 0,
    dependencies: ACTION_DEPENDENCIES[actionId] || []
  };
};

/**
 * Obtient le label d'une action
 * @param {string} actionId - ID de l'action
 * @returns {string} Label de l'action
 */
export const getActionLabel = (actionId) => {
  return ACTION_METADATA[actionId]?.label || actionId;
};

/**
 * Obtient la description d'une action
 * @param {string} actionId - ID de l'action
 * @returns {string} Description de l'action
 */
export const getActionDescription = (actionId) => {
  return ACTION_METADATA[actionId]?.description || '';
};

/**
 * Vérifie si une action a des dépendances
 * @param {string} actionId - ID de l'action
 * @returns {boolean} True si l'action a des dépendances
 */
export const hasActionDependencies = (actionId) => {
  return (ACTION_DEPENDENCIES[actionId] || []).length > 0;
};

/**
 * Obtient les dépendances d'une action
 * @param {string} actionId - ID de l'action
 * @returns {string[]} Liste des IDs des actions dépendantes
 */
export const getActionDependencies = (actionId) => {
  return ACTION_DEPENDENCIES[actionId] || [];
};

/**
 * Vérifie si les dépendances d'une action sont satisfaites
 * @param {string} actionId - ID de l'action
 * @param {Object} currentPermissions - Permissions actuelles pour la ressource
 * @returns {boolean} True si toutes les dépendances sont satisfaites
 */
export const areDependenciesSatisfied = (actionId, currentPermissions) => {
  const dependencies = getActionDependencies(actionId);
  return dependencies.every(dep => currentPermissions[dep] === true);
};

/**
 * Active automatiquement les dépendances d'une action
 * @param {string} actionId - ID de l'action à activer
 * @param {Object} currentPermissions - Permissions actuelles pour la ressource
 * @returns {Object} Nouvelles permissions avec dépendances activées
 */
export const enableActionWithDependencies = (actionId, currentPermissions) => {
  const newPermissions = { ...currentPermissions };
  const dependencies = getActionDependencies(actionId);
  
  // Activer toutes les dépendances
  dependencies.forEach(dep => {
    newPermissions[dep] = true;
  });
  
  // Activer l'action principale
  newPermissions[actionId] = true;
  
  return newPermissions;
};

/**
 * Désactive les actions qui dépendent d'une action désactivée
 * @param {string} actionId - ID de l'action désactivée
 * @param {Object} currentPermissions - Permissions actuelles pour la ressource
 * @returns {Object} Nouvelles permissions avec dépendances désactivées
 */
export const disableDependentActions = (actionId, currentPermissions) => {
  const newPermissions = { ...currentPermissions };
  
  // Trouver toutes les actions qui dépendent de cette action
  Object.entries(ACTION_DEPENDENCIES).forEach(([dependentAction, deps]) => {
    if (deps.includes(actionId)) {
      newPermissions[dependentAction] = false;
    }
  });
  
  return newPermissions;
};

/**
 * Valide la cohérence des permissions pour une ressource
 * @param {Object} permissions - Permissions à valider
 * @returns {Object} Résultat de validation avec erreurs et warnings
 */
export const validatePermissions = (permissions) => {
  const errors = [];
  const warnings = [];
  
  Object.entries(permissions).forEach(([actionId, isEnabled]) => {
    if (isEnabled) {
      const dependencies = getActionDependencies(actionId);
      
      // Vérifier les dépendances
      dependencies.forEach(dep => {
        if (!permissions[dep]) {
          errors.push({
            action: actionId,
            message: `L'action "${getActionLabel(actionId)}" nécessite l'action "${getActionLabel(dep)}"`,
            dependency: dep
          });
        }
      });
    }
  });
  
  return { isValid: errors.length === 0, errors, warnings };
};

/**
 * Obtient toutes les actions triées par niveau
 * @returns {Array} Actions triées par niveau croissant
 */
export const getActionsByLevel = () => {
  return Object.entries(ACTION_METADATA)
    .map(([id, metadata]) => ({ id, ...metadata }))
    .sort((a, b) => a.level - b.level);
};

/**
 * Obtient le niveau maximum des permissions activées
 * @param {Object} permissions - Permissions à analyser
 * @returns {number} Niveau maximum
 */
export const getMaxPermissionLevel = (permissions) => {
  let maxLevel = 0;
  
  Object.entries(permissions).forEach(([actionId, isEnabled]) => {
    if (isEnabled) {
      const level = ACTION_METADATA[actionId]?.level || 0;
      if (level > maxLevel) {
        maxLevel = level;
      }
    }
  });
  
  return maxLevel;
};

/**
 * Vérifie si les permissions incluent une action de gestion complète
 * @param {Object} permissions - Permissions à vérifier
 * @returns {boolean} True si manage est activé
 */
export const hasManagePermission = (permissions) => {
  return permissions.manage === true;
};

/**
 * Vérifie si les permissions incluent des actions d'écriture
 * @param {Object} permissions - Permissions à vérifier
 * @returns {boolean} True si write ou create ou delete est activé
 */
export const hasWritePermissions = (permissions) => {
  return permissions.write === true || permissions.create === true || permissions.delete === true;
};

/**
 * Vérifie si les permissions sont en lecture seule
 * @param {Object} permissions - Permissions à vérifier
 * @returns {boolean} True si seule la lecture est activée
 */
export const isReadOnly = (permissions) => {
  return permissions.read === true && 
         !hasWritePermissions(permissions) && 
         !hasManagePermission(permissions);
};

/**
 * Formate les permissions pour l'affichage
 * @param {Object} permissions - Permissions à formater
 * @returns {Array} Liste formatée des permissions activées
 */
export const formatPermissionsForDisplay = (permissions) => {
  return Object.entries(permissions)
    .filter(([_, isEnabled]) => isEnabled)
    .map(([actionId]) => getActionLabel(actionId))
    .sort();
};

/**
 * Crée un objet de permissions par défaut (toutes désactivées)
 * @returns {Object} Permissions par défaut
 */
export const createDefaultPermissions = () => {
  return Object.values(ACTIONS).reduce((acc, action) => {
    acc[action] = false;
    return acc;
  }, {});
};

export default {
  getActionInfo,
  getActionLabel,
  getActionDescription,
  hasActionDependencies,
  getActionDependencies,
  areDependenciesSatisfied,
  enableActionWithDependencies,
  disableDependentActions,
  validatePermissions,
  getActionsByLevel,
  getMaxPermissionLevel,
  hasManagePermission,
  hasWritePermissions,
  isReadOnly,
  formatPermissionsForDisplay,
  createDefaultPermissions
};
